import { useEffect, useMemo, useState } from 'react'
import { api } from '../api/client'
import type { FxRate, FxRateQuery } from '../api/client'
import { useHasPermission } from '../api/session'
import { useListParams } from '../hooks/useListParams'
import { useListQuery } from '../hooks/useListQuery'
import { Card } from '../components/card/Card'
import { Button } from '../components/common/Button'
import { Input, Select } from '../components/common/Controls'
import { EmptyState, ErrorBox } from '../components/common/Feedback'
import { FilterBar, FilterEnd, FilterFixed } from '../components/common/FilterBar'
import { PermissionNotice } from '../components/common/PermissionNotice'
import { DataTable } from '../components/table/DataTable'
import type { Column } from '../components/table/types'
import s from './FxRatesPage.module.scss'

/**
 * Wechselkurse — die Referenzkurse der EZB.
 *
 * KEIN BÖRSENKURS. Die EZB stellt täglich gegen 16:00 Frankfurter Zeit einen
 * Wert fest; es gibt keinen Verlauf innerhalb eines Tages, kein Geld und
 * Brief, keine Wochenenden.
 */

const PERMISSION = 'fx_rate:read'
const PAGE_SIZE = 50

/** Wie weit die Liste beim Öffnen zurückreicht. */
const VORLAUF_TAGE = 90

const datum = new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium' })

/**
 * Vier Nachkommastellen, wie die EZB sie veröffentlicht.
 *
 * Nicht zwei: Bei 1,1664 wäre die dritte Stelle bereits eine Kursbewegung,
 * die man wegrundete. Bei JPY (185,60) sind es dagegen zwei — deshalb wird
 * nicht fest formatiert, sondern die gelieferte Genauigkeit übernommen.
 */
function kurs(wert: string): string {
  const stellen = (wert.split('.')[1] ?? '').length
  return Number(wert).toLocaleString('de-DE', {
    minimumFractionDigits: Math.min(Math.max(stellen, 2), 6),
    maximumFractionDigits: Math.min(Math.max(stellen, 2), 6),
  })
}

/**
 * Die Veränderung zum vorigen Bankarbeitstag.
 *
 * Der Wert kommt aus der Datenbank, nicht aus der Nachbarzeile: Hier läge nur
 * die aktuelle Seite, und die erste Zeile jeder Seite hätte keinen Vorgänger.
 *
 * `null` bekommt einen Strich, keine Null — die wäre eine Aussage, die
 * niemand getroffen hat. Es trifft genau eine Zeile: die älteste.
 */
function Aenderung({ wert }: { wert: string | null }) {
  if (wert === null) return <span className={s.ohne}>—</span>
  const zahl = Number(wert)
  const ton = zahl > 0 ? 'plus' : zahl < 0 ? 'minus' : 'null'
  return (
    <span className={s.aenderung} data-ton={ton}>
      {zahl > 0 ? '+' : ''}{zahl.toLocaleString('de-DE', {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      })} %
    </span>
  )
}

const COLUMNS: Column<FxRate>[] = [
  { key: 'rate_date', label: 'Tag', sortable: true, mono: true,
    minWidth: '128px',
    render: (row) => datum.format(new Date(row.rate_date)),
    help: 'Der Tag, an dem die EZB den Kurs FESTGESTELLT hat — nicht der Tag des Abrufs. Nur Bankarbeitstage: Für Samstag und Sonntag gibt es keinen Referenzkurs.' },
  { key: 'pair', label: 'Paar', minWidth: '110px',
    help: 'Von welcher Währung in welche. Die EZB rechnet alles gegen den Euro; ein direktes USD/JPY gibt es dort nicht.' },
  { key: 'rate', label: 'Kurs', sortable: true, mono: true, align: 'right',
    minWidth: '130px', copy: (row) => row.rate,
    render: (row) => kurs(row.rate),
    help: 'Fremdwährung je EINEN Euro: 1,1664 heißt 1 EUR = 1,1664 USD. Der Referenzkurs wird werktags gegen 16:00 Frankfurter Zeit festgestellt.' },
  { key: 'change', label: 'Änderung', mono: true, align: 'right',
    minWidth: '110px',
    render: (row) => <Aenderung wert={row.change} />,
    help: 'Gegenüber dem vorigen Bankarbeitstag — nach einem Wochenende also gegenüber Freitag. Der Vorgänger wird VOR dem Zeitraumfilter bestimmt: Wer ab dem 20. filtert, sieht beim 20. trotzdem die Änderung gegenüber dem 19. Ein Strich hat nur die älteste Zeile des Bestands.' },
]

/** Das Datum von vor N Tagen als JJJJ-MM-TT. */
function vorTagen(tage: number): string {
  const tag = new Date()
  tag.setDate(tag.getDate() - tage)
  return tag.toISOString().slice(0, 10)
}

export function FxRatesPage() {
  const allowed = useHasPermission(PERMISSION)

  // Die Vorbelegung steht FEST, solange die Seite offen ist. Würde sie bei
  // jedem Rendern neu gerechnet, wechselte der Filterwert um Mitternacht
  // mitten in der Sitzung — und `touched` vergliche gegen ein bewegliches
  // Ziel.
  const seit = useMemo(() => vorTagen(VORLAUF_TAGE), [])

  const list = useListParams({
    filters: { pair: '', von: '', bis: '' },
    preset: { von: seit },
    sort: 'rate_date',
    // Eine Zeitreihe wird von hinten gelesen.
    descending: true,
  })

  // Das Paar steht in der Adresse als `EUR/USD` — ein Wert, den ein Mensch
  // liest. Der Dienst will zwei Felder.
  const [basis, gegen] = (list.filters.pair || '/').split('/')

  const params: FxRateQuery = {
    base: basis || undefined,
    quote: gegen || undefined,
    von: list.filters.von || undefined,
    bis: list.filters.bis || undefined,
    sort: list.sort as FxRateQuery['sort'],
    dir: list.descending ? 'desc' : 'asc',
    page: list.page,
    size: PAGE_SIZE,
    total: true,
  }

  const { result, loading, error, denied } = useListQuery(
    api.fxRates, params, allowed)

  // Die Paare einmal geladen — sie ändern sich nur, wenn eine neue Währung
  // dazukommt, nicht beim Blättern oder Filtern.
  const [paare, setPaare] = useState<string[]>([])
  useEffect(() => {
    if (!allowed) return
    let aktuell = true
    api.fxPairs()
      .then((antwort) => { if (aktuell) setPaare(antwort) })
      .catch(() => { /* Bleibt die Liste leer, filtert man eben nicht. */ })
    return () => { aktuell = false }
  }, [allowed])

  if (!allowed || denied) {
    return <PermissionNotice permission={PERMISSION}
                             subject="die Wechselkurse" />
  }

  return (
    <>
      <FilterBar>
        <FilterFixed>
          <Select label="Paar" value={list.filters.pair}
                  disabled={paare.length === 0}
                  onChange={(event) =>
                    list.setFilter('pair', event.target.value)}>
            <option value="">alle Paare</option>
            {paare.map((paar) => (
              <option key={paar} value={paar}>{paar}</option>
            ))}
          </Select>
        </FilterFixed>
        <FilterFixed>
          <Input label="Von" type="date" value={list.filters.von}
                 onChange={(event) => list.setFilter('von', event.target.value)} />
        </FilterFixed>
        <FilterFixed>
          <Input label="Bis" type="date" value={list.filters.bis}
                 onChange={(event) => list.setFilter('bis', event.target.value)} />
        </FilterFixed>
        <FilterEnd>
          <Button onClick={list.reset} disabled={!list.touched}>
            Zurücksetzen
          </Button>
        </FilterEnd>
      </FilterBar>

      {error && <ErrorBox>{error}</ErrorBox>}

      <Card flush span={12}>
        <DataTable
          columns={COLUMNS}
          rows={result?.items ?? []}
          rowKey={(row) => row.id}
          sort={{ key: list.sort, descending: list.descending }}
          onSort={(next) => list.setSort(next.key, next.descending)}
          page={result ? { page: result.page, size: result.size,
                           total: result.total, hasMore: result.has_more }
                       : undefined}
          onPage={list.setPage}
          loading={loading && result === null}
          empty={<EmptyState title="keine Kurse">
            {list.touched
              ? 'Kein Kurs liegt in diesem Zeitraum.'
              : `Für die letzten ${VORLAUF_TAGE} Tage liegt kein Kurs vor. `
                + 'Gefüllt wird die Tabelle über die Importstrecke.'}
          </EmptyState>}
        />
      </Card>
    </>
  )
}
